import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Copy,
  FileText,
  Lock,
  MessageSquare,
  PenTool,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { SkeletonGrid } from '../../components/Skeleton';
import { useFeature, upgradeHintFor } from '../../lib/features';
import { toast } from '../../lib/toast';

const SUB_TABS = [
  { key: 'capsules', label: 'Answer Capsules', icon: Sparkles, endpoint: 'answer-capsules' },
  { key: 'faqs', label: 'FAQs', icon: MessageSquare, endpoint: 'faqs' },
  { key: 'prompts', label: 'Rewriter Prompts', icon: PenTool, endpoint: 'rewriter-prompts' },
  { key: 'briefs', label: 'Brief Templates', icon: FileText, endpoint: 'briefs' },
];

function copyToClipboard(text, what = 'Content') {
  navigator.clipboard.writeText(text);
  toast.success(`${what} copied`);
}

export default function LibraryTab() {
  const unlocked = useFeature('dfyContentLibrary');
  const [active, setActive] = useState('capsules');
  const [industry, setIndustry] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset filters when switching sub-tabs to avoid stale UI state.
  useEffect(() => {
    setIndustry('');
    setSearchInput('');
    setQuery('');
  }, [active]);

  const metaQuery = useQuery({
    queryKey: ['dfy-meta'],
    queryFn: () => api.get('/api/dfy/meta').then((r) => r.data),
    enabled: unlocked,
    retry: false,
  });

  if (!unlocked) {
    return (
      <div className="space-y-5">
        <PageHeader
          variant="gradient"
          icon={BookOpen}
          eyebrow="Booster Pack"
          title="DFY content library"
          description="Plug-and-play schema templates, Answer Capsules, FAQ Q&A, rewriter prompts, and brief skeletons across 21 industries."
        />
        <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Lock className="w-4 h-4" />
              </span>
              Locked
              <span className="pill bg-gray-100 text-gray-500">Booster Pack</span>
            </div>
          </div>
          <p className="text-sm text-ink-600">
            The DFY content library is part of the GEO Booster Pack ($27 order bump).
            Unlock 200+ Answer Capsules, 500+ FAQs, 12 Rewriter Prompts (Authority Voice,
            Local-First, Product-Focused…), and industry-specific Brief Templates that
            give your writers an instant page skeleton.
          </p>
          <p className="text-xs text-ink-400 mt-2">
            Upgrade required: {upgradeHintFor('dfyContentLibrary')}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        variant="gradient"
        icon={BookOpen}
        eyebrow="Booster Pack"
        title="DFY content library"
        description="Browse, search, and copy ready-to-paste content across industries. Everything here is curated for AI-search visibility."
      />

      {/* Sub-tab bar with counts */}
      <div className="card">
        <div className="flex items-center gap-1 flex-wrap border-b border-gray-100 -mx-6 px-6 pb-3 mb-4 overflow-x-auto scroll-soft">
          {SUB_TABS.map((tab) => {
            const Icon = tab.icon;
            const count = metaQuery.data?.counts?.[
              tab.key === 'capsules'
                ? 'answerCapsules'
                : tab.key === 'faqs'
                  ? 'faqs'
                  : tab.key === 'prompts'
                    ? 'rewriterPrompts'
                    : 'briefs'
            ];
            return (
              <button
                key={tab.key}
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition ${
                  active === tab.key
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'text-ink-600 hover:bg-gray-50'
                }`}
                onClick={() => setActive(tab.key)}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {typeof count === 'number' && (
                  <span className="text-xs text-ink-400 font-normal">({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10 max-w-xs"
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {active !== 'prompts' && metaQuery.data?.industries && (
            <select
              className="input max-w-[200px]"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              <option value="">All industries</option>
              {metaQuery.data.industries.map((i) => (
                <option key={i.slug} value={i.slug}>
                  {i.label}
                </option>
              ))}
            </select>
          )}
          {(query || industry) && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setIndustry('');
              }}
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        <LibraryList active={active} industry={industry} query={query} industries={metaQuery.data?.industries || []} />
      </div>
    </div>
  );
}

function LibraryList({ active, industry, query, industries }) {
  const tab = SUB_TABS.find((t) => t.key === active);
  const params = new URLSearchParams();
  if (industry) params.set('industry', industry);
  if (query) params.set('q', query);
  params.set('limit', '100');

  const queryResult = useQuery({
    queryKey: ['dfy', active, industry, query],
    queryFn: () => api.get(`/api/dfy/${tab.endpoint}?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const rows = queryResult.data?.rows || [];
  const total = queryResult.data?.total ?? 0;
  const industryLabel = (slug) => industries.find((i) => i.slug === slug)?.label || slug;

  if (queryResult.isLoading) return <SkeletonGrid count={4} />;
  if (!rows.length) {
    return (
      <EmptyState
        icon={tab.icon}
        title="No matches in this library."
        body={query || industry ? 'Try clearing filters.' : 'Nothing curated for this section yet.'}
      />
    );
  }

  return (
    <>
      <div className="text-xs text-ink-500 mb-3">
        {total} {tab.label.toLowerCase()}
      </div>
      <div className="space-y-3">
        {active === 'capsules' && rows.map((r) => <CapsuleCard key={r._id} row={r} industryLabel={industryLabel} />)}
        {active === 'faqs' && rows.map((r) => <FaqCard key={r._id} row={r} industryLabel={industryLabel} />)}
        {active === 'prompts' && rows.map((r) => <PromptCard key={r._id} row={r} />)}
        {active === 'briefs' && rows.map((r) => <BriefCard key={r._id} row={r} industryLabel={industryLabel} />)}
      </div>
    </>
  );
}

function CapsuleCard({ row, industryLabel }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4 hover:border-brand-200 transition">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pill bg-brand-50 text-brand-700">{industryLabel(row.industry)}</span>
          <span className="text-xs text-ink-500 font-medium">{row.topic}</span>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => copyToClipboard(row.body, 'Answer Capsule')}
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <p className="text-sm text-ink-700 leading-relaxed">{row.body}</p>
    </div>
  );
}

function FaqCard({ row, industryLabel }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4 hover:border-brand-200 transition">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pill bg-amber-50 text-amber-700">{industryLabel(row.industry)}</span>
          <span className="text-xs text-ink-500 font-medium">{row.topic}</span>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() =>
            copyToClipboard(`Q: ${row.question}\nA: ${row.answer}`, 'FAQ')
          }
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <div className="text-sm font-medium text-ink-900 mb-1">{row.question}</div>
      <p className="text-sm text-ink-700 leading-relaxed">{row.answer}</p>
    </div>
  );
}

function PromptCard({ row }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4 hover:border-brand-200 transition">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-medium text-ink-900">{row.name}</span>
          {row.voiceTone && (
            <span className="pill bg-violet-50 text-violet-700">{row.voiceTone}</span>
          )}
        </div>
        <button
          type="button"
          className="btn-ghost text-xs flex-shrink-0"
          onClick={() => copyToClipboard(row.systemPrompt, 'Rewriter prompt')}
        >
          <Copy className="w-3 h-3" /> Copy prompt
        </button>
      </div>
      <p className="text-sm text-ink-700 leading-relaxed mb-2">{row.description}</p>
      {row.bestFor && (
        <p className="text-xs text-ink-500">
          <span className="font-semibold">Best for:</span> {row.bestFor}
        </p>
      )}
      <details className="mt-2">
        <summary className="text-xs text-ink-500 cursor-pointer select-none">
          Show system prompt
        </summary>
        <pre className="mt-2 text-xs whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-ink-700">
          {row.systemPrompt}
        </pre>
      </details>
    </div>
  );
}

function BriefCard({ row, industryLabel }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4 hover:border-brand-200 transition">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="pill bg-cyan-50 text-cyan-700">{industryLabel(row.industry)}</span>
          <span className="font-medium text-ink-900 truncate">{row.title}</span>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs flex-shrink-0"
          onClick={() =>
            copyToClipboard(
              [
                `# ${row.title}`,
                row.description,
                `Target word count: ${row.targetWordCount}`,
                '',
                '## Outline',
                ...(row.outline || []).map((h, i) => `${i + 1}. ${h}`),
                '',
                '## Primary keywords',
                ...(row.primaryKeywords || []),
                '',
                '## Answer capsule seed',
                row.answerCapsuleSeed,
                '',
                '## FAQ ideas',
                ...(row.faqsHint || []),
              ].join('\n'),
              'Brief',
            )
          }
        >
          <Copy className="w-3 h-3" /> Copy brief
        </button>
      </div>
      {row.description && <p className="text-sm text-ink-700 mb-2">{row.description}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="font-semibold text-ink-700 mb-1">Word count target</div>
          <div className="text-ink-500">{row.targetWordCount}</div>
        </div>
        <div>
          <div className="font-semibold text-ink-700 mb-1">H2 outline ({row.outline?.length || 0})</div>
          <ul className="text-ink-500 space-y-0.5">
            {(row.outline || []).slice(0, 4).map((h, i) => (
              <li key={i}>{i + 1}. {h}</li>
            ))}
            {row.outline?.length > 4 && (
              <li className="text-ink-400">+{row.outline.length - 4} more…</li>
            )}
          </ul>
        </div>
        <div>
          <div className="font-semibold text-ink-700 mb-1">FAQ ideas</div>
          <ul className="text-ink-500 space-y-0.5">
            {(row.faqsHint || []).slice(0, 3).map((f, i) => (
              <li key={i}>· {f}</li>
            ))}
            {row.faqsHint?.length > 3 && (
              <li className="text-ink-400">+{row.faqsHint.length - 3} more…</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
